import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './Services.css';

function Services() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = 'Services | Sapium Beauty - Skin & Laser Treatments East London';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Explore Sapium Beauty services in East London (Canning Town): laser hair removal, advanced facials, chemical peels, PRX-33, BioRePeel, microneedling and more at 28 Barking Rd, London E16 1EQ.'
      );
    }
  }, []);

  const services = [
    {
      id: 'facial',
      title: t('services.treatments.facial.title'),
      description: t('services.treatments.facial.description'),
      benefits: t('services.treatments.facial.benefits', { returnObjects: true }),
      duration: t('services.durations.facial'),
      price: t('services.prices.facial'),
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'chemical-peels',
      title: t('services.treatments.chemical-peels.title'),
      description: t('services.treatments.chemical-peels.description'),
      benefits: t('services.treatments.chemical-peels.benefits', { returnObjects: true }),
      duration: t('services.durations.chemical-peels'),
      price: t('services.prices.chemical-peels'),
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'prx-33',
      title: t('services.treatments.prx-33.title'),
      description: t('services.treatments.prx-33.description'),
      benefits: t('services.treatments.prx-33.benefits', { returnObjects: true }),
      duration: t('services.durations.prx-33'),
      price: t('services.prices.prx-33'),
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'microneedling',
      title: t('services.treatments.microneedling.title'),
      description: t('services.treatments.microneedling.description'),
      benefits: t('services.treatments.microneedling.benefits', { returnObjects: true }),
      duration: t('services.durations.microneedling'),
      price: t('services.prices.microneedling'),
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'dermaplaning',
      title: t('services.treatments.dermaplaning.title'),
      description: t('services.treatments.dermaplaning.description'),
      benefits: t('services.treatments.dermaplaning.benefits', { returnObjects: true }),
      duration: t('services.durations.dermaplaning'),
      price: t('services.prices.dermaplaning'),
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'mesotherapy',
      title: t('services.treatments.mesotherapy.title'),
      description: t('services.treatments.mesotherapy.description'),
      benefits: t('services.treatments.mesotherapy.benefits', { returnObjects: true }),
      duration: t('services.durations.mesotherapy'),
      price: t('services.prices.mesotherapy'),
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'mandelic-acid-peel',
      title: 'Mandelic Acid Peel',
      description: 'A gentle AHA peel derived from bitter almonds, ideal for sensitive skin and darker skin tones. Works on the surface to brighten, smooth, and restore clarity with minimal downtime.',
      benefits: [
        'Reduces pigmentation and dark spots',
        'Improves uneven skin tone',
        'Minimises blemishes and congestion',
        'Refines pores and skin texture',
        'Suitable for all skin types including sensitive'
      ],
      duration: '45-60 minutes',
      price: 'From £85',
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'azelaic-acid-peel',
      title: 'Azelaic Acid Peel',
      description: 'Designed for sensitive, acne-prone, and rosacea-affected skin. Gently reduces inflammation, unclogs pores, and brightens uneven tone without aggressive exfoliation.',
      benefits: [
        'Reduces acne and breakouts',
        'Calms redness and irritation',
        'Brightens pigmentation',
        'Controls excess oil',
        'Suitable for sensitive skin types'
      ],
      duration: '45-60 minutes',
      price: 'From £85',
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'glow-peel',
      title: 'Glow Peel',
      description: 'A gentle yet effective resurfacing treatment designed to restore luminosity, refine texture, and boost hydration — perfect before special events or whenever your skin needs an instant pick-me-up.',
      benefits: [
        'Reveals immediate glow and radiance',
        'Smooths fine lines and rough texture',
        'Minimises pores and congestion',
        'Brightens dull, uneven complexion',
        'Little to no downtime'
      ],
      duration: '45-60 minutes',
      price: 'From £85',
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'microneedling-biorepeel',
      title: 'Microneedling + BioRePeel',
      description: 'A powerful dual approach combining microneedling for deep collagen induction with BioRePeel for surface and mid-dermis renewal. Significantly improves skin clarity, firmness, and radiance.',
      benefits: [
        'Strong collagen induction plus chemical skin renewal',
        'Increased product penetration for superior outcomes',
        'Enhanced brightness and smoother texture',
        'Natural results with low discomfort',
        'Optimal for stronger clinical results'
      ],
      duration: '45-60 minutes',
      price: 'From £150',
      location: t('services.locations.mainTreatments')
    }
  ];

  return (
    <div className="services-page">
      <section className="services-hero">
        <div className="container">
          <h1>{t('services.hero.title')}</h1>
          <p>{t('services.hero.description')}</p>
        </div>
      </section>

      <section className="services-grid">
        <div className="container">
          {services.map((service) => (
            <div key={service.id} className="service-card">
              <h2>{service.title}</h2>
              <p className="service-description">{service.description}</p>
              
              <div className="service-details">
                <div className="benefits">
                  <h3>{t('services.labels.benefits')}</h3>
                  <ul>
                    {(Array.isArray(service.benefits) ? service.benefits : []).map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="treatment-info">
                  <div className="info-item">
                    <span className="label">{t('services.labels.duration')}</span>
                    <span className="value">{service.duration}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">{t('services.labels.price')}</span>
                    <span className="value">{service.price}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">{t('services.labels.location')}</span>
                    <span className="value">{service.location}</span>
                  </div>
                </div>
              </div>
              
              {service.id === 'chemical-peels' && (
                <Link to="/services/chemical-peels" className="service-detail-button">
                  Learn More
                </Link>
              )}
              
              {service.id === 'prx-33' && (
                <Link to="/services/biorepeel" className="service-detail-button">
                  Learn More
                </Link>
              )}
              
              {service.id === 'mandelic-acid-peel' && (
                <Link to="/services/mandelic-acid-peel" className="service-detail-button">
                  Learn More
                </Link>
              )}
              
              {service.id === 'azelaic-acid-peel' && (
                <Link to="/services/azelaic-acid-peel" className="service-detail-button">
                  Learn More
                </Link>
              )}
              
              {service.id === 'glow-peel' && (
                <Link to="/services/glow-peel" className="service-detail-button">
                  Learn More
                </Link>
              )}
              
              {service.id === 'microneedling' && (
                <Link to="/services/microneedling" className="service-detail-button">
                  Learn More
                </Link>
              )}
              
              {service.id === 'microneedling-biorepeel' && (
                <Link to="/services/microneedling-biorepeel" className="service-detail-button">
                  Learn More
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="services-grid">
        <div className="container">
          <div className="service-card" style={{ gridColumn: '1 / -1', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', border: '2px solid #d4af37' }}>
            <h2>{t('services.locations.laserHairRemovalTitle')}</h2>
            <p className="service-description">
              {t('services.locations.laserHairRemovalDescription')}
            </p>
            <div className="service-details">
              <div className="benefits" style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ color: '#d4af37', marginBottom: '0.75rem' }}>{t('services.locations.technologyTitle')}</h3>
                <p style={{ fontSize: '1rem', lineHeight: '1.6', color: '#555' }}>
                  {t('services.locations.laserHairRemovalTechnology')}
                </p>
              </div>
              <div className="treatment-info">
                <div className="info-item">
                  <span className="label">{t('services.labels.location')}</span>
                  <span className="value">{t('services.locations.laserHairRemoval')}</span>
                </div>
                <div className="info-item">
                  <span className="label" style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>Note:</span>
                  <span className="value" style={{ fontSize: '0.9rem' }}>{t('services.locations.laserHairRemovalNote')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>{t('services.cta.title')}</h2>
          <p>{t('services.cta.description')}</p>
          <a href="https://www.fresha.com/book-now/sapium-beauty-hu8f8ild/all-offer?share=true&pId=2743251" target="_blank" rel="noopener noreferrer" className="cta-button">{t('services.cta.button')}</a>
        </div>
      </section>
    </div>
  );
}

export default Services; 